/* errorReporter — front-end error capture + batched dispatch to
   the backend telemetry endpoint at POST /telemetry/client_error.

   Hooks four sources:
     - window.onerror (uncaught synchronous JS errors)
     - window.onunhandledrejection (rejected promises with no .catch)
     - ErrorBoundary.componentDidCatch (React render errors — wired
       directly in ErrorBoundary.jsx by importing reportReactError)
     - axios response interceptor (5xx + network errors — wired in
       authInterceptor.jsx by calling reportHttpError)

   Defenses against flooding our own telemetry:
     - In-memory dedup by hash of (message + first 3 stack lines + route)
     - Per-minute throttle (max MAX_PER_MINUTE distinct reports)
     - Batched flush every FLUSH_INTERVAL_MS so we don't burn one
       request per error
     - Sanitization pass strips Authorization headers from extras and
       masks email/phone-shaped substrings out of messages and stacks

   Kill switch: REACT_APP_TELEMETRY_DISABLED=1 short-circuits everything
   so we can stop reporting without a deploy if the endpoint melts. */

import axiosClient from "../apis/axios-client/axiosClient.jsx";

const ENDPOINT = "/telemetry/client_error";
const FLUSH_INTERVAL_MS = 5000;
const MAX_PER_MINUTE = 10;
const MAX_BATCH_SIZE = 20;
const MAX_MESSAGE_LEN = 2000;
const MAX_STACK_LEN = 8000;

const state = {
  enabled: process.env.REACT_APP_TELEMETRY_DISABLED !== "1",
  queue: [],
  recentHashes: new Map(),
  windowStart: Date.now(),
  windowCount: 0,
  flushTimer: null,
  /* "unknown" sentinel per spec §6 — backend prefers a stable
     marker over an empty string when /version.json fails. */
  appVersion: "unknown",
  authToken: null,
};

/* App.js calls this once we've fetched /version.json so the report
   carries the deploy id alongside the error. */
export function setReporterAppVersion(version) {
  state.appVersion = version || "";
}

/* App.js / Dashboard pass the live JWT through to the reporter so
   reports get correlated to the user. Cleared on logout. */
export function setReporterAuthToken(token) {
  state.authToken = token || null;
}

/* Hash for dedup. Includes the first 3 stack lines so two distinct
   call sites with the same message are reported separately. */
function hashOf(message, stack, route) {
  const stackHead = (stack || "").split("\n").slice(0, 3).join("|");
  return `${message}::${stackHead}::${route}`;
}

/* Conservative PII scrubber. We never want emails, phone numbers, or
   bearer tokens leaving the client unredacted. False positives are
   fine; false negatives are not. */
function sanitize(s) {
  if (typeof s !== "string") return s;
  return s
    .replace(/Bearer\s+[A-Za-z0-9._-]+/g, "Bearer [REDACTED]")
    .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, "[email]")
    .replace(/(\+?\d[\d\s().-]{7,}\d)/g, "[phone]");
}

/* Trim to a sane length so a runaway error doesn't ship megabytes
   of stack trace. */
function clip(s, max) {
  if (typeof s !== "string") return s;
  return s.length > max ? s.slice(0, max) + "…[clipped]" : s;
}

/* Spec §4 also asks for a ~50-line cap on stacks. Apply both: line
   cap first (cuts deep stacks) then char cap (catches a single
   pathologically long line). */
const MAX_STACK_LINES = 50;
function clipStack(s) {
  if (typeof s !== "string") return s;
  const lines = s.split("\n");
  const truncated = lines.length > MAX_STACK_LINES;
  const head = truncated ? lines.slice(0, MAX_STACK_LINES).join("\n") + "\n…[stack clipped]" : s;
  return clip(head, MAX_STACK_LEN);
}

function withinThrottle() {
  const now = Date.now();
  if (now - state.windowStart > 60000) {
    state.windowStart = now;
    state.windowCount = 0;
  }
  if (state.windowCount >= MAX_PER_MINUTE) return false;
  state.windowCount += 1;
  return true;
}

function enqueue(report) {
  if (!state.enabled) return;
  if (!report || !report.error_message) return;
  const route = report.route || (typeof window !== "undefined" ? window.location.pathname : "");
  const h = hashOf(report.error_message, report.error_stack, route);

  /* Suppress duplicates within a 60s sliding window. Keeps a
     repeated render-loop error from overwhelming the queue. */
  const lastSeen = state.recentHashes.get(h);
  const now = Date.now();
  if (lastSeen && now - lastSeen < 60000) return;
  state.recentHashes.set(h, now);

  /* Periodically prune old hashes so the map doesn't grow forever. */
  if (state.recentHashes.size > 200) {
    for (const [k, t] of state.recentHashes) {
      if (now - t > 60000) state.recentHashes.delete(k);
    }
  }

  if (!withinThrottle()) return;

  const sanitized = {
    error_type: report.error_type || "manual",
    severity: report.severity || "error",
    error_message: clip(sanitize(report.error_message), MAX_MESSAGE_LEN),
    error_stack: clipStack(sanitize(report.error_stack || "")),
    error_source: clip(sanitize(report.error_source || ""), 500),
    route,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    viewport: typeof window !== "undefined" ? `${window.innerWidth}x${window.innerHeight}` : "",
    app_version: state.appVersion,
    occurred_at: new Date().toISOString(),
    extra: report.extra ? sanitizeExtra(report.extra) : null,
  };

  state.queue.push(sanitized);
  if (state.queue.length >= MAX_BATCH_SIZE) flushNow();
  else scheduleFlush();
}

function sanitizeExtra(extra) {
  try {
    const cloned = JSON.parse(JSON.stringify(extra));
    walkAndSanitize(cloned);
    return cloned;
  } catch {
    return null;
  }
}

function walkAndSanitize(node) {
  if (!node || typeof node !== "object") return;
  for (const k of Object.keys(node)) {
    const v = node[k];
    /* Strip authorization headers wherever they appear. */
    if (typeof k === "string" && /authorization|cookie|set-cookie/i.test(k)) {
      node[k] = "[REDACTED]";
    } else if (typeof v === "string") {
      node[k] = sanitize(v);
    } else if (typeof v === "object") {
      walkAndSanitize(v);
    }
  }
}

function scheduleFlush() {
  if (state.flushTimer) return;
  state.flushTimer = setTimeout(flushNow, FLUSH_INTERVAL_MS);
}

function flushNow() {
  if (state.flushTimer) {
    clearTimeout(state.flushTimer);
    state.flushTimer = null;
  }
  if (state.queue.length === 0) return;
  /* Drain the queue one-report-per-POST so the contract matches
     the spec: backend expects `{ ...report }` body, returns
     `{ report_id: "uuid" }` per request. Spec §3 asks us to
     space sequential sends ~100ms apart so the rate-limit budget
     (60/min/client server-side) drains gracefully on burst. */
  const batch = state.queue.splice(0, MAX_BATCH_SIZE);
  const headers = state.authToken
    ? { Authorization: `Bearer ${state.authToken}` }
    : {};
  batch.forEach((report, i) => {
    setTimeout(() => {
      axiosClient.post(ENDPOINT, report, { headers }).catch((e) => {
        const status = e?.response?.status;
        if (status === 429) {
          const retryAfter = Number(e.response.headers?.["retry-after"]) || 60;
          state.windowStart = Date.now() + retryAfter * 1000 - 60000;
          state.windowCount = MAX_PER_MINUTE;
        }
      });
    }, i * 100);
  });
}

/* ── Public reporters ── */

export function reportError(message, options = {}) {
  enqueue({
    error_type: options.type || "manual",
    severity: options.severity || "error",
    error_message: String(message ?? ""),
    error_stack: options.stack || (new Error()).stack,
    error_source: options.source || "",
    extra: options.extra || null,
  });
}

export function reportReactError(error, info, caughtAt) {
  enqueue({
    error_type: "react_render",
    severity: "error",
    error_message: error?.message || String(error),
    error_stack: (error?.stack || "") + "\n--componentStack--\n" + (info?.componentStack || ""),
    error_source: caughtAt || "",
    extra: { caughtAt },
  });
}

/* Strip query string + fragment from a URL — spec §4 forbids
   sending query params (they may carry tokens) but the path is fine. */
function urlPathOnly(url) {
  if (typeof url !== "string") return "";
  const q = url.indexOf("?");
  const h = url.indexOf("#");
  let end = url.length;
  if (q !== -1) end = Math.min(end, q);
  if (h !== -1) end = Math.min(end, h);
  return url.slice(0, end);
}

export function reportHttpError(error) {
  const status = error?.response?.status;
  /* Only report 5xx + transport errors. 4xx is usually bad input
     or auth flow noise (the 401 interceptor handles those). */
  if (status && status < 500) return;
  const rawUrl = error?.config?.url || "";
  const url = urlPathOnly(rawUrl);
  const method = (error?.config?.method || "").toUpperCase();
  enqueue({
    error_type: "http_error",
    severity: "error",
    error_message: `HTTP ${status || "network"} ${method} ${url}`,
    error_stack: error?.stack || "",
    error_source: url,
    /* Per spec §4: response_status + URL path only. No request body,
       no response body — even a clipped excerpt is forbidden because
       the backend response can echo unredacted user input. */
    extra: {
      request_url: url,
      request_method: method,
      response_status: status || null,
    },
  });
}

/* ── Global handler installation ──
   Idempotent: safe to call multiple times. App.js installs once on
   componentDidMount; ErrorBoundary and authInterceptor call the
   reporters directly, no global hook needed. */
let installed = false;
export function installGlobalErrorHandlers() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    /* React's dev overlay also fires here in dev — suppress when an
       ErrorBoundary already caught the same error. */
    if (window.errorHandledByBoundary) return;
    enqueue({
      error_type: "uncaught",
      severity: "error",
      error_message: event?.message || (event?.error && event.error.message) || "Uncaught error",
      error_stack: event?.error?.stack || "",
      error_source: `${event?.filename || ""}:${event?.lineno || 0}:${event?.colno || 0}`,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    enqueue({
      error_type: "unhandled_rejection",
      severity: "error",
      error_message: reason?.message || String(reason || "Unhandled promise rejection"),
      error_stack: reason?.stack || "",
    });
  });

  /* Best-effort flush on page unload using sendBeacon if available
     so we don't lose queued reports when the user navigates away. */
  window.addEventListener("beforeunload", () => {
    if (state.queue.length === 0) return;
    try {
      if (navigator.sendBeacon && process.env.REACT_APP_BASE_API_URL) {
        const blob = new Blob(
          [JSON.stringify({ reports: state.queue })],
          { type: "application/json" },
        );
        navigator.sendBeacon(process.env.REACT_APP_BASE_API_URL + ENDPOINT, blob);
        state.queue = [];
      }
    } catch { /* best effort */ }
  });
}

/* Test/diagnostic only — the queue is otherwise opaque. */
export function _debugQueueSize() {
  return state.queue.length;
}
